import { useEffect, useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { api, formatMoney } from '@/lib/api';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { BarChart, Bar, PieChart, Pie, Cell, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend } from 'recharts';
import { Receipt, Coins, Wallet, WarningCircle, TrendUp, Buildings, UsersThree, ListChecks, FileText, PencilSimple, Check, ArrowUp, ArrowDown, Plus, Trash, Palette, ArrowClockwise } from '@phosphor-icons/react';
import { EditCardDialog, AddCardDialog } from '@/components/DashboardDialogs';

const PIE_COLORS = ['#0F172A', '#B4935A', '#3B82F6', '#059669', '#DC2626'];

const COLOR_PALETTE = [
  { name: 'slate',   value: '#0F172A', bg: 'bg-slate-900',    ring: 'ring-slate-900' },
  { name: 'gold',    value: '#B4935A', bg: 'bg-[#B4935A]',    ring: 'ring-[#B4935A]' },
  { name: 'emerald', value: '#059669', bg: 'bg-emerald-600',  ring: 'ring-emerald-600' },
  { name: 'red',     value: '#DC2626', bg: 'bg-red-600',      ring: 'ring-red-600' },
  { name: 'blue',    value: '#2563EB', bg: 'bg-blue-600',     ring: 'ring-blue-600' },
  { name: 'amber',   value: '#D97706', bg: 'bg-amber-600',    ring: 'ring-amber-600' },
  { name: 'purple',  value: '#7C3AED', bg: 'bg-purple-600',   ring: 'ring-purple-600' },
  { name: 'teal',    value: '#0D9488', bg: 'bg-teal-600',     ring: 'ring-teal-600' },
];

const SIZE_MAP = {
  sm: 'col-span-1',
  md: 'col-span-1 md:col-span-2',
  lg: 'col-span-2 md:col-span-3',
  xl: 'col-span-2 md:col-span-4',
};

// Card catalog — data-driven from summary
const CARD_CATALOG = {
  collection_rate:  { title: 'نسبة التحصيل',    icon: TrendUp,       kind: 'kpi',    color: '#B4935A', size: 'sm', filter: null,                              format: (v) => `${(v || 0).toFixed(1)}%` },
  total_invoices:   { title: 'إجمالي الفواتير',  icon: Receipt,       kind: 'kpi',    color: '#0F172A', size: 'sm', filter: {},                                format: (v) => (v || 0).toLocaleString() },
  collected_amount: { title: 'المبلغ المحصل',   icon: Coins,         kind: 'kpi',    color: '#059669', size: 'sm', filter: { status: 'Fully Paid' },          format: formatMoney },
  remaining_amount: { title: 'المبلغ المتبقي',  icon: Wallet,        kind: 'kpi',    color: '#DC2626', size: 'sm', filter: { status: 'Unpaid' },              format: formatMoney },
  open_invoices:    { title: 'فواتير مفتوحة',    icon: Receipt,       kind: 'kpi',    color: '#0F172A', size: 'sm', filter: { status: 'Unpaid' },              format: (v) => (v || 0).toLocaleString() },
  overdue_invoices: { title: 'فواتير متأخرة',    icon: WarningCircle, kind: 'kpi',    color: '#DC2626', size: 'sm', filter: { status: 'Unpaid' },              format: (v) => (v || 0).toLocaleString() },
  paid_invoices:    { title: 'فواتير مغلقة',     icon: Receipt,       kind: 'kpi',    color: '#059669', size: 'sm', filter: { status: 'Fully Paid' },          format: (v) => (v || 0).toLocaleString() },
  open_tasks:       { title: 'المهام المفتوحة',   icon: ListChecks,    kind: 'kpi',    color: '#B4935A', size: 'sm', filter: null,                              format: (v) => (v || 0).toLocaleString(), route: '/tasks' },
  branches_chart:   { title: 'أداء الفروع',      icon: Buildings,     kind: 'chart',  color: '#0F172A', size: 'lg' },
  status_pie:       { title: 'توزيع الحالات',    icon: TrendUp,       kind: 'chart',  color: '#B4935A', size: 'md' },
  quarterly_grid:   { title: 'الأرباع السنوية',   icon: TrendUp,       kind: 'chart',  color: '#B4935A', size: 'xl' },
};

const DEFAULT_LAYOUT = {
  cards: [
    { id: 'c1', key: 'collection_rate',  color: '#B4935A', size: 'sm', visible: true },
    { id: 'c2', key: 'total_invoices',   color: '#0F172A', size: 'sm', visible: true },
    { id: 'c3', key: 'collected_amount', color: '#059669', size: 'sm', visible: true },
    { id: 'c4', key: 'remaining_amount', color: '#DC2626', size: 'sm', visible: true },
    { id: 'c5', key: 'open_invoices',    color: '#0F172A', size: 'sm', visible: true },
    { id: 'c6', key: 'overdue_invoices', color: '#DC2626', size: 'sm', visible: true },
    { id: 'c7', key: 'paid_invoices',    color: '#059669', size: 'sm', visible: true },
    { id: 'c8', key: 'open_tasks',       color: '#B4935A', size: 'sm', visible: true },
    { id: 'ch1', key: 'branches_chart',  color: '#0F172A', size: 'lg', visible: true },
    { id: 'ch2', key: 'status_pie',      color: '#B4935A', size: 'md', visible: true },
    { id: 'ch3', key: 'quarterly_grid',  color: '#B4935A', size: 'xl', visible: true },
  ],
};

const genId = () => 'c_' + Math.random().toString(36).slice(2, 9);

const KpiCard = ({ card, data, editMode, onEdit, onDelete, onMoveUp, onMoveDown, nav }) => {
  const cat = CARD_CATALOG[card.key];
  if (!cat) return null;
  const Icon = cat.icon;
  const value = cat.format ? cat.format(data?.[card.key]) : data?.[card.key];
  const title = card.title || cat.title;

  const onClick = () => {
    if (editMode) return;
    if (cat.route) return nav(cat.route);
    if (cat.filter) return nav('/invoices?' + new URLSearchParams(cat.filter).toString());
    nav('/invoices');
  };

  return (
    <div className={`${SIZE_MAP[card.size] || SIZE_MAP.sm} relative`}>
      <button
        type="button"
        onClick={onClick}
        disabled={editMode}
        className="w-full bg-white border rounded-md p-5 text-start hover:border-[#B4935A] transition-colors relative overflow-hidden"
        style={{ borderColor: '#E2E8F0' }}
      >
        <div className="absolute top-0 inset-inline-start-0 w-full h-1" style={{ backgroundColor: card.color }} />
        <div className="flex items-start justify-between mb-3">
          <div className="text-xs text-slate-500 uppercase tracking-wider">{title}</div>
          <div className="w-9 h-9 rounded-md flex items-center justify-center" style={{ backgroundColor: card.color + '15' }}>
            <Icon size={18} style={{ color: card.color }} weight="regular" />
          </div>
        </div>
        <div className="text-2xl md:text-3xl font-bold tabular-nums" style={{ color: card.color }}>{value ?? '—'}</div>
      </button>
      {editMode && (
        <div className="absolute top-2 left-2 flex gap-1 bg-white border border-slate-200 rounded-md shadow-sm p-1">
          <button onClick={onMoveUp} className="p-1 hover:bg-slate-100 rounded" title="لأعلى"><ArrowUp size={14} /></button>
          <button onClick={onMoveDown} className="p-1 hover:bg-slate-100 rounded" title="لأسفل"><ArrowDown size={14} /></button>
          <button onClick={onEdit} className="p-1 hover:bg-slate-100 rounded"><PencilSimple size={14} /></button>
          <button onClick={onDelete} className="p-1 hover:bg-red-100 rounded"><Trash size={14} className="text-red-600" /></button>
        </div>
      )}
    </div>
  );
};

const ChartCard = ({ card, data, editMode, onEdit, onDelete, onMoveUp, onMoveDown }) => {
  const cat = CARD_CATALOG[card.key];
  if (!cat) return null;
  const title = card.title || cat.title;

  let content = null;
  if (card.key === 'branches_chart') {
    content = (
      <ResponsiveContainer width="100%" height={280}>
        <BarChart data={data?.by_branch || []}>
          <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
          <XAxis dataKey="branch" tick={{ fontSize: 11, fill: '#64748B' }} />
          <YAxis tick={{ fontSize: 11, fill: '#64748B' }} />
          <Tooltip contentStyle={{ fontSize: 12, direction: 'rtl' }} />
          <Legend wrapperStyle={{ fontSize: 12 }} />
          <Bar dataKey="collected" name="المحصل" fill={card.color} radius={[3, 3, 0, 0]} />
          <Bar dataKey="remaining" name="المتبقي" fill="#94A3B8" radius={[3, 3, 0, 0]} />
        </BarChart>
      </ResponsiveContainer>
    );
  } else if (card.key === 'status_pie') {
    content = (
      <ResponsiveContainer width="100%" height={280}>
        <PieChart>
          <Pie data={data?.status_distribution || []} dataKey="count" nameKey="status" cx="50%" cy="50%" outerRadius={90} label>
            {(data?.status_distribution || []).map((entry, i) => <Cell key={entry.status || `st-${i}`} fill={PIE_COLORS[i % PIE_COLORS.length]} />)}
          </Pie>
          <Tooltip contentStyle={{ fontSize: 12 }} />
          <Legend wrapperStyle={{ fontSize: 12 }} />
        </PieChart>
      </ResponsiveContainer>
    );
  } else if (card.key === 'quarterly_grid') {
    content = (
      <div className="grid grid-cols-4 gap-4">
        {(data?.by_quarter || []).map((q) => (
          <div key={q.quarter} className="border border-slate-200 rounded-md p-4">
            <div className="text-xs text-slate-500 mb-1">{q.quarter}</div>
            <div className="text-2xl font-bold tabular-nums" style={{ color: card.color }}>{(q.rate || 0).toFixed(1)}%</div>
            <div className="text-xs text-slate-500 mt-2">محصل: <span className="tabular-nums text-emerald-600">{formatMoney(q.collected)}</span></div>
            <div className="text-xs text-slate-500">إجمالي: <span className="tabular-nums">{formatMoney(q.total)}</span></div>
          </div>
        ))}
      </div>
    );
  }

  return (
    <div className={`${SIZE_MAP[card.size] || SIZE_MAP.lg} relative`}>
      <Card className="p-5 border border-slate-200 rounded-md shadow-none">
        <div className="mb-4 flex items-start justify-between">
          <div>
            <div className="text-xs uppercase tracking-widest" style={{ color: card.color }}>Chart</div>
            <div className="text-lg font-semibold text-slate-900">{title}</div>
          </div>
        </div>
        {content}
      </Card>
      {editMode && (
        <div className="absolute top-2 left-2 flex gap-1 bg-white border border-slate-200 rounded-md shadow-sm p-1">
          <button onClick={onMoveUp} className="p-1 hover:bg-slate-100 rounded"><ArrowUp size={14} /></button>
          <button onClick={onMoveDown} className="p-1 hover:bg-slate-100 rounded"><ArrowDown size={14} /></button>
          <button onClick={onEdit} className="p-1 hover:bg-slate-100 rounded"><PencilSimple size={14} /></button>
          <button onClick={onDelete} className="p-1 hover:bg-red-100 rounded"><Trash size={14} className="text-red-600" /></button>
        </div>
      )}
    </div>
  );
};

export default function Dashboard() {
  const [data, setData] = useState(null);
  const [layout, setLayout] = useState(DEFAULT_LAYOUT);
  const [loading, setLoading] = useState(true);
  const [editMode, setEditMode] = useState(false);
  const [editingCard, setEditingCard] = useState(null);
  const [addOpen, setAddOpen] = useState(false);
  const nav = useNavigate();

  useEffect(() => {
    api.get('/dashboard/summary').then(({ data }) => setData(data)).finally(() => setLoading(false));
    api.get('/user-preferences/dashboard').then(({ data }) => {
      if (data.layout?.cards?.length) setLayout(data.layout);
    }).catch(() => {});
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const save = (next) => {
    setLayout(next);
    api.put('/user-preferences/dashboard', next).catch(() => {});
  };

  const visible = useMemo(() => layout.cards.filter(c => c.visible !== false), [layout]);

  const move = (idx, dir) => {
    const next = [...layout.cards];
    const t = idx + dir;
    if (t < 0 || t >= next.length) return;
    [next[idx], next[t]] = [next[t], next[idx]];
    save({ ...layout, cards: next });
  };

  const removeCard = (id) => {
    if (!window.confirm('حذف البطاقة؟')) return;
    save({ ...layout, cards: layout.cards.filter(c => c.id !== id) });
  };

  const updateCard = (updated) => {
    save({ ...layout, cards: layout.cards.map(c => c.id === updated.id ? updated : c) });
    setEditingCard(null);
  };

  const addCard = (key) => {
    const cat = CARD_CATALOG[key];
    if (!cat) return;
    save({ ...layout, cards: [...layout.cards, { id: genId(), key, color: cat.color, size: cat.size, visible: true }] });
    setAddOpen(false);
  };

  const resetDefault = () => {
    if (!window.confirm('إعادة الضبط إلى التصميم الافتراضي؟')) return;
    save(DEFAULT_LAYOUT);
  };

  if (loading || !data) return <div className="text-slate-500 text-sm">جاري التحميل...</div>;

  const usedKeys = new Set(layout.cards.map(c => c.key));
  const availableToAdd = Object.keys(CARD_CATALOG).filter(k => !usedKeys.has(k) || CARD_CATALOG[k].kind === 'kpi');

  return (
    <div className="space-y-6" data-testid="dashboard-page">
      <div className="flex items-end justify-between flex-wrap gap-4">
        <div>
          <div className="text-xs uppercase tracking-widest text-[#B4935A] mb-1">Executive Overview</div>
          <h1 className="text-3xl md:text-4xl font-bold text-slate-900">لوحة التحكم</h1>
          <p className="text-sm text-slate-500 mt-1">نظرة عامة قابلة للتخصيص — عدّل الألوان والأحجام وأضف أو احذف البطاقات</p>
        </div>
        <div className="flex items-center gap-2">
          <div className="text-xs text-slate-500 me-2">آخر تحديث: {new Date().toLocaleString('ar-SA')}</div>
          {editMode ? (
            <>
              <Button size="sm" variant="outline" onClick={() => setAddOpen(true)} data-testid="add-card-button"><Plus className="ms-1" size={14} /> إضافة</Button>
              <Button size="sm" variant="outline" onClick={resetDefault}><ArrowClockwise className="ms-1" size={14} /> افتراضي</Button>
              <Button size="sm" onClick={() => setEditMode(false)} className="bg-emerald-600 text-white hover:bg-emerald-700" data-testid="save-layout-button"><Check className="ms-1" size={14} /> إنهاء</Button>
            </>
          ) : (
            <Button size="sm" variant="outline" onClick={() => setEditMode(true)} data-testid="customize-button"><PencilSimple className="ms-1" size={14} /> تخصيص</Button>
          )}
        </div>
      </div>

      {editMode && (
        <div className="p-3 border border-[#B4935A]/40 bg-[#B4935A]/5 rounded-md text-sm text-slate-700 flex items-center gap-2">
          <Palette size={16} className="text-[#B4935A]" />
          وضع التخصيص مُفعّل — استخدم الأزرار أعلى كل بطاقة لتغيير الترتيب أو اللون أو الحجم أو الحذف.
        </div>
      )}

      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 items-stretch">
        {visible.map((card, idx) => {
          const cat = CARD_CATALOG[card.key];
          if (!cat) return null;
          const props = {
            card, data, editMode,
            onEdit: () => setEditingCard(card),
            onDelete: () => removeCard(card.id),
            onMoveUp: () => move(layout.cards.indexOf(card), -1),
            onMoveDown: () => move(layout.cards.indexOf(card), 1),
            nav,
          };
          return cat.kind === 'kpi' ? <KpiCard key={card.id} {...props} /> : <ChartCard key={card.id} {...props} />;
        })}
      </div>

      {/* Edit card dialog */}
      <EditCardDialog
        open={!!editingCard}
        onOpenChange={(v) => !v && setEditingCard(null)}
        card={editingCard}
        setCard={setEditingCard}
        onSave={updateCard}
        catalog={CARD_CATALOG}
        palette={COLOR_PALETTE}
      />

      {/* Add card dialog */}
      <AddCardDialog
        open={addOpen}
        onOpenChange={setAddOpen}
        availableKeys={availableToAdd}
        catalog={CARD_CATALOG}
        onAdd={addCard}
      />
    </div>
  );
}
